import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface ScheduleTask {
  id: number;
  workId: number;
  name: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  statusId: number;
  statusCode: string;
  statusName: string;
  responsible?: string | null;
  billingMilestoneId?: number | null;
  billingMilestoneDescription?: string | null;
  subcontractorId?: number | null;
  subcontractorName?: string | null;
  subcontractorEmail?: string | null;
  deadlineChangePending: boolean;
  previousEndDate?: string | null;
  lastNotifiedAt?: string | null;
  sortOrder: number;
  notes?: string | null;
  created: string;
  lastModified: string;
}

export interface ScheduleTaskPayload {
  workId: number;
  name: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  statusId: number;
  responsible?: string | null;
  billingMilestoneId?: number | null;
  subcontractorId?: number | null;
  sortOrder: number;
  notes?: string | null;
}

export interface ScheduleTaskStatus {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

export function getScheduleTasks(workId?: number): Promise<ScheduleTask[]> {
  const query =
    workId !== undefined && workId !== null && Number.isFinite(workId)
      ? `?workId=${workId}`
      : "";
  return request<ScheduleTask[]>(`/api/scheduletasks${query}`);
}

export function getScheduleTaskById(taskId: number): Promise<ScheduleTask> {
  return request<ScheduleTask>(`/api/scheduletasks/${taskId}`);
}

export function getScheduleTaskStatuses(): Promise<ScheduleTaskStatus[]> {
  return request<ScheduleTaskStatus[]>("/api/scheduletasks/statuses");
}

export function createScheduleTask(
  payload: ScheduleTaskPayload,
): Promise<ScheduleTask> {
  return request<ScheduleTask>("/api/scheduletasks", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateScheduleTask(
  taskId: number,
  payload: ScheduleTaskPayload & { id: number },
): Promise<ScheduleTask> {
  return request<ScheduleTask>(`/api/scheduletasks/${taskId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function notifyScheduleTaskDelay(taskId: number): Promise<ScheduleTask> {
  return request<ScheduleTask>(
    `/api/scheduletasks/${taskId}/notify-delay`,
    { method: "POST" },
  );
}

export async function deleteScheduleTask(taskId: number): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/scheduletasks/${taskId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
